/**
 * Knowledge base for the resume chat agent.
 * Retrieval: queries are tokenized and scored against each entry's keywords —
 * `strong` keywords count 3x, regular keywords 1x, exact phrase hits add a bonus.
 * The best entry above the threshold answers; otherwise a help message lists topics.
 */

export type KBEntry = {
  id: string;
  strong: string[];
  keywords: string[];
  phrases?: string[];
  answer: string;
};

export const KNOWLEDGE: KBEntry[] = [
  {
    id: 'whoami',
    strong: ['who', 'about', 'mujahid', 'introduce', 'yourself', 'summary', 'overview'],
    keywords: ['profile', 'bio', 'background', 'tell'],
    phrases: ['who is', 'about him', 'tell me about'],
    answer:
      'Mujahid Masood is a Senior Software Engineer — Backend & Applied AI, based in Germany, with 10+ years of full-time engineering experience since 2011. By day he builds cloud-native JVM backends (Kotlin, Quarkus, Kubernetes, AWS); on his own time he ships AI-agent systems like Keepsake and Meshyay, plus apps live on the Play Store and App Store.'
  },
  {
    id: 'experience',
    strong: ['experience', 'career', 'work', 'job', 'history', 'employers', 'worked'],
    keywords: ['years', 'timeline', 'roles', 'companies', 'professional'],
    phrases: ['work history', 'where has he worked'],
    answer:
      '10+ years of full-time backend engineering since 2011 (plus working-student roles through an MSc):\n• beyonnex.io (2024–now, Hamburg/remote) — IoT energy-metering backend: Kotlin, Quarkus, Kubernetes, AWS IoT Core\n• Testo SE (2021–2024, Berlin) — Back End Developer: Java 17/21, Spring Boot, Terraform on AWS+Azure, Datadog\n• evia (2019–2021, Stuttgart) — Back End Developer: Spring Boot microservices for Audi, Credit Plus and Lidl\n• 360T (2017–2019, Frankfurt) — FX market trading platform\n• asuro (2016), GBM / eGovernment Authority Bahrain (2015)\n• American Express (2013–2014, client engagement via Royal Cyber) — Card Member Remittance System, payment settlement\n• Royal Cyber (2011–2014) — IBM WebSphere/BPM.\nFull detail on the Experience page (1:experience).'
  },
  {
    id: 'current-role',
    strong: ['beyonnex', 'current', 'currently', 'now', 'today'],
    keywords: ['role', 'position', 'iot', 'energy', 'metering', 'hamburg'],
    phrases: ['current job', 'where does he work now'],
    answer:
      'Mujahid is a Software Engineer at beyonnex.io (Hamburg, remote) since July 2024, building the cloud-native backend of an IoT energy-metering platform — Kotlin and Quarkus services on Kubernetes and AWS, ingesting live sensor data via AWS IoT Core, MQTT, and Mbus Gateway 6. He is open to senior backend and applied-AI roles.'
  },
  {
    id: 'keepsake',
    strong: ['keepsake', 'dementia', 'memory', 'companion'],
    keywords: ['voice', 'family', 'patient', 'care', 'safety', 'fleet', 'ten'],
    phrases: ['voice companion'],
    answer:
      'Keepsake is a voice-first memory companion for a person living with dementia, plus a portal for her family. Ten cooperating agents run her day — but every decision that could hurt her if wrong is made by code, not a prompt: medication questions never reach a model, and agent-rewritten prompts only go live after passing coded checks. Deployed on Google Cloud Run.\nLive: https://keepsake-776070314074.europe-west1.run.app\nDemo: https://vimeo.com/1222237493'
  },
  {
    id: 'meshyay',
    strong: ['meshyay', 'marketplace', 'ecommerce', 'commerce', 'storefront'],
    keywords: ['usdc', 'payouts', 'circle', 'supplier', 'autonomy', 'flutter', 'shop', 'xprize'],
    phrases: ['meshyay.com'],
    answer:
      'Meshyay (meshyay.com) is a commerce platform Mujahid built end to end: one Flutter codebase for web, Android and iOS carrying three surfaces \u2014 shopper storefront, supplier portal, and an admin console \u2014 on a FastAPI backend. Its distinguishing feature is the AI agent layer that runs the shop\u2019s back office, where what an agent may do without a human is an explicit register in code (autonomy.py) rather than a deployment boundary. A companion project settles supplier payouts autonomously in USDC via Circle Agent Wallet, within a human-set policy cap \u2014 built for the Build with Gemini XPRIZE (Agentic Economy Prize): github.com/mujahidmasood/meshyay-agentic-usdc'
  },
  {
    id: 'ai-projects',
    strong: ['ai', 'agents', 'agent', 'llm', 'projects'],
    keywords: ['portfolio', 'side', 'built', 'building', 'ml', 'machine'],
    phrases: ['ai projects', 'agent systems', 'what has he built'],
    answer:
      'Mujahid\'s applied-AI portfolio:\n• Keepsake — ten-agent voice companion for dementia care, safety decisions in code not prompts\n• Meshyay — commerce platform whose back office is run by AI agents bounded by an autonomy register in code, with autonomous USDC supplier payouts\nAsk me about any of them by name, or see the Projects page (2:projects).'
  },
  {
    id: 'products',
    strong: ['tijarat360', 'tijarat', 'builder', 'breakers', 'kabutar', 'apps', 'games'],
    keywords: ['shipped', 'store', 'play', 'android', 'ios', 'marketplace', 'game', 'unity', 'flutter'],
    phrases: ['app store', 'play store', 'shipped products'],
    answer:
      'Shipped products, all live on the Play Store and App Store:\n• Tijarat360 — B2B agricultural marketplace for Pakistan with smart-contract escrow (React/Capacitor, FastAPI, Solidity) — tijarat360.com\n• Builder Breakers — Unity puzzle/tower-defense game with an AI-driven economy — builderbreakers.fun\n• Kabutar Ud — Flutter multiplayer game with real-time sync and TTS — kabutar.chatwithme.fun'
  },
  {
    id: 'skills',
    strong: ['skills', 'stack', 'technologies', 'tech', 'kotlin', 'java', 'quarkus'],
    keywords: ['languages', 'tools', 'spring', 'python', 'typescript', 'know', 'knows'],
    phrases: ['tech stack', 'what can he do'],
    answer:
      'Core stack:\n• Languages: Kotlin, Java, Python, TypeScript\n• Backend: Quarkus, Spring Boot, GraphQL, microservices\n• Cloud: AWS (incl. IoT Core), Kubernetes, Docker, Terraform\n• Data: MySQL, MongoDB, DynamoDB, Kafka\n• AI engineering: LLM agents, Claude & Gemini APIs, Google ADK, guardrails, RAG, MCP\nFull list on the Skills page (3:skills).'
  },
  {
    id: 'cloud',
    strong: ['aws', 'kubernetes', 'cloud', 'iot', 'k8s', 'infrastructure'],
    keywords: ['docker', 'terraform', 'mqtt', 'lorawan', 'devops'],
    answer:
      'Cloud & infrastructure is one of Mujahid\'s deepest areas: AWS (EC2, S3, DynamoDB, Kinesis, IoT Core, CloudWatch), Kubernetes and Docker in production, Terraform/Terragrunt for IaC, and IoT connectivity with MQTT, LoRaWAN, and Mbus Gateway 6. He holds a DevOps on AWS specialization (Coursera/AWS, 2023).'
  },
  {
    id: 'education',
    strong: ['education', 'degree', 'university', 'msc', 'bsc', 'study', 'studied', 'darmstadt'],
    keywords: ['thesis', 'master', 'bachelor', 'nuces', 'fast'],
    answer:
      'Education:\n• MSc Computer Science (Distributed Software Systems), TU Darmstadt, 2015–2019 — thesis: an automated exam grader using ML and computer vision (github.com/mujahidmasood/automated-exam-grader)\n• BSc Computer Science, FAST-NUCES Islamabad, 2007–2011, graduated with honors.'
  },
  {
    id: 'certifications',
    strong: ['certification', 'certifications', 'certificate', 'certified'],
    keywords: ['coursera', 'ibm', 'bpm', 'specialization'],
    answer:
      'Certifications:\n• Introducing Generative AI with AWS — Udacity, 2025\n• DevOps on AWS Specialization — Coursera / Amazon Web Services, 2023\n• AI Programming with Python — Udacity, 2017\n• Data Structures and Algorithms — Udacity, 2017\n• IBM Business Process Manager Certified Developer — IBM, 2014'
  },
  {
    id: 'contact',
    strong: ['contact', 'email', 'hire', 'hiring', 'reach', 'linkedin', 'github', 'available', 'availability'],
    keywords: ['touch', 'recruit', 'open', 'cv', 'resume', 'interview'],
    phrases: ['get in touch', 'open to work'],
    answer:
      'Mujahid is open to senior backend and applied-AI roles.\n• Email: mujahid.mms@gmail.com\n• LinkedIn: linkedin.com/in/mujahidmasood\n• GitHub: github.com/mujahidmasood\n• Resume PDF: mujahidmasood.com/Mujahid_Masood_Resume.pdf (or type `cv` in the terminal)'
  },
  {
    id: 'location',
    strong: ['location', 'where', 'based', 'germany', 'relocate', 'remote'],
    keywords: ['live', 'lives', 'city', 'country', 'hamburg', 'berlin'],
    answer:
      'Mujahid is based in Germany and has worked there since 2016 (Frankfurt, Stuttgart, Berlin, and now remotely for beyonnex.io in Hamburg). He currently works fully remote and is open to remote or hybrid senior roles.'
  },
  {
    id: 'greeting',
    strong: ['hi', 'hello', 'hey', 'salam', 'hallo'],
    keywords: ['morning', 'evening', 'greetings'],
    answer:
      'Hey! I\'m the resume agent on this site. Ask me things like "what is Keepsake?", "what\'s his tech stack?", "where has he worked?", or "how do I contact him?"'
  },
  {
    id: 'thanks',
    strong: ['thanks', 'thank', 'cool', 'awesome', 'great', 'nice'],
    keywords: ['bye', 'goodbye', 'cheers'],
    answer: 'Anytime! If you want to go deeper, mujahid.mms@gmail.com is the fastest route to the human behind this terminal.'
  }
];

const HELP =
  'I can answer questions about:\n• experience & current role\n• AI projects — Keepsake, Meshyay\n• shipped apps — Tijarat360, Builder Breakers, Kabutar Ud\n• skills & cloud stack\n• education & certifications\n• contact / hiring\nTry: "what is Keepsake?" or "what\'s his tech stack?"';

function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(t => t.length > 1);
}

export function askKnowledgeBase(query: string): string {
  const q = query.toLowerCase();
  const tokens = new Set(tokenize(query));
  if (tokens.size === 0) return HELP;

  let best: KBEntry | null = null;
  let bestScore = 0;

  for (const entry of KNOWLEDGE) {
    let score = 0;
    for (const k of entry.strong) if (tokens.has(k)) score += 3;
    for (const k of entry.keywords) if (tokens.has(k)) score += 1;
    for (const p of entry.phrases || []) if (q.includes(p)) score += 4;
    if (score > bestScore) {
      bestScore = score;
      best = entry;
    }
  }

  return bestScore >= 3 && best ? best.answer : HELP;
}
